import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileArchive, Eye, Printer } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toCsv, downloadBlob, downloadZip } from "@/project/lib/report-export";
import { inr, shortDate } from "@/project/lib/format";

export const Route = createFileRoute("/reports")({
  head: () => ({
    meta: [
      { title: "Reports & Analytics · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Period-wise assistance totals by category, organization and month, with CSV export for trustees.",
      },
      { property: "og:title", content: "Assistance Reports & Analytics" },
      {
        property: "og:description",
        content:
          "Assistance totals by category, organization and month with CSV export.",
      },
    ],
  }),
  component: ReportsPage,
});

function ReportsPage() {
  const firstOfYear = new Date(new Date().getFullYear(), 0, 1)
    .toISOString()
    .slice(0, 10);
  const [from, setFrom] = useState(firstOfYear);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [openId, setOpenId] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["reports", from, to],
    queryFn: async () => {
      const [{ data: apps }, { data: letters }] = await Promise.all([
        supabase
          .from("applications")
          .select("*, assistance_categories(name)")
          .gte("application_date", from)
          .lte("application_date", to)
          .order("application_date", { ascending: false }),
        supabase
          .from("generated_letters")
          .select(
            "organization_name, letter_date, valid_until, application_id, applications(ref_no, applicant_name)"
          )
          .gte("letter_date", from)
          .lte("letter_date", to),
      ]);
      return { apps: apps ?? [], letters: letters ?? [] };
    },
  });

  const apps = data?.apps ?? [];
  const openApp = apps.find(a => a.id === openId) ?? null;
  const totals = useMemo(() => {
    const sum = (
      k: "total_requirement" | "our_contribution" | "balance_required"
    ) => apps.reduce((s, a) => s + Number(a[k] ?? 0), 0);
    return {
      count: apps.length,
      requirement: sum("total_requirement"),
      given: sum("our_contribution"),
      balance: sum("balance_required"),
    };
  }, [apps]);

  const byCategory = useMemo(() => {
    const m = new Map<
      string,
      { count: number; given: number; requirement: number }
    >();
    for (const a of apps) {
      const k = a.assistance_categories?.name ?? "Uncategorised";
      const p = m.get(k) ?? { count: 0, given: 0, requirement: 0 };
      m.set(k, {
        count: p.count + 1,
        given: p.given + Number(a.our_contribution ?? 0),
        requirement: p.requirement + Number(a.total_requirement ?? 0),
      });
    }
    return [...m.entries()].sort((a, b) => b[1].given - a[1].given);
  }, [apps]);

  const byMonth = useMemo(() => {
    const m = new Map<string, { count: number; given: number }>();
    for (const a of apps) {
      const k = a.application_date.slice(0, 7);
      const p = m.get(k) ?? { count: 0, given: 0 };
      m.set(k, {
        count: p.count + 1,
        given: p.given + Number(a.our_contribution ?? 0),
      });
    }
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [apps]);

  const byOrg = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of data?.letters ?? []) {
      const name = l.organization_name ?? "Unknown";
      m.set(name, (m.get(name) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [data?.letters]);

  const byCategoryStatus = useMemo(() => {
    const forwarded = new Set((data?.letters ?? []).map(l => l.application_id));
    const m = new Map<
      string,
      {
        received: number;
        forwarded: number;
        pending: number;
        requirement: number;
        given: number;
      }
    >();
    for (const a of apps) {
      const k = a.assistance_categories?.name ?? "Uncategorised";
      const p = m.get(k) ?? {
        received: 0,
        forwarded: 0,
        pending: 0,
        requirement: 0,
        given: 0,
      };
      const isForwarded =
        forwarded.has(a.id) || a.status === "Letter Generated";
      m.set(k, {
        received: p.received + 1,
        forwarded: p.forwarded + (isForwarded ? 1 : 0),
        pending: p.pending + (isForwarded ? 0 : 1),
        requirement: p.requirement + Number(a.total_requirement ?? 0),
        given: p.given + Number(a.our_contribution ?? 0),
      });
    }
    return [...m.entries()].sort((a, b) => b[1].received - a[1].received);
  }, [apps, data?.letters]);

  const categoryReport = useMemo(() => {
    const forwarded = new Set((data?.letters ?? []).map(l => l.application_id));
    type Row = {
      received: number;
      forwarded: number;
      requirement: number;
      given: number;
      subs: Map<string, { received: number; forwarded: number; given: number }>;
    };
    const m = new Map<string, Row>();
    for (const a of apps) {
      const k = a.assistance_categories?.name ?? "Uncategorised";
      const row: Row = m.get(k) ?? {
        received: 0,
        forwarded: 0,
        requirement: 0,
        given: 0,
        subs: new Map(),
      };
      const isFwd = forwarded.has(a.id) || a.status === "Letter Generated";
      row.received += 1;
      row.forwarded += isFwd ? 1 : 0;
      row.requirement += Number(a.total_requirement ?? 0);
      row.given += Number(a.our_contribution ?? 0);
      const sk = a.subcategory?.trim() || "General";
      const s = row.subs.get(sk) ?? { received: 0, forwarded: 0, given: 0 };
      row.subs.set(sk, {
        received: s.received + 1,
        forwarded: s.forwarded + (isFwd ? 1 : 0),
        given: s.given + Number(a.our_contribution ?? 0),
      });
      m.set(k, row);
    }
    const rows = [...m.entries()].sort((a, b) => b[1].received - a[1].received);
    const totals = rows.reduce(
      (t, [, v]) => ({
        received: t.received + v.received,
        forwarded: t.forwarded + v.forwarded,
        requirement: t.requirement + v.requirement,
        given: t.given + v.given,
      }),
      { received: 0, forwarded: 0, requirement: 0, given: 0 }
    );
    return { rows, totals };
  }, [apps, data?.letters]);

  const exportCategoryCsv = () =>
    downloadBlob(
      new Blob(
        [
          toCsv(
            [
              "Category",
              "Subcategory",
              "Applications Received",
              "Applications Forwarded",
              "Pending",
              "Forwarded %",
              "Assistance Given",
            ],
            categoryReport.rows.flatMap(([k, v]) => [
              [
                k,
                "— All —",
                v.received,
                v.forwarded,
                v.received - v.forwarded,
                v.received ? Math.round((v.forwarded / v.received) * 100) : 0,
                v.given,
              ],
              ...[...v.subs.entries()].map(([sk, sv]) => [
                k,
                sk,
                sv.received,
                sv.forwarded,
                sv.received - sv.forwarded,
                sv.received
                  ? Math.round((sv.forwarded / sv.received) * 100)
                  : 0,
                sv.given,
              ]),
            ])
          ),
        ],
        { type: "text/csv" }
      ),
      `category-wise-report-${from}-to-${to}.csv`
    );

  const appHead = [
    "Ref No",
    "Date",
    "Applicant",
    "Ration Card",
    "Category",
    "Total Requirement",
    "Own Contribution",
    "Our Contribution",
    "Other Received",
    "Balance Required",
    "Status",
  ];
  const appRows = () =>
    apps.map(a => [
      a.ref_no,
      a.application_date,
      a.applicant_name,
      a.ration_card_no,
      a.assistance_categories?.name ?? "",
      a.total_requirement,
      a.applicant_contribution,
      a.our_contribution,
      a.other_received,
      a.balance_required,
      a.status,
    ]);

  const exportCsv = () =>
    downloadBlob(
      new Blob([toCsv(appHead, appRows())], { type: "text/csv" }),
      `assistance-report-${from}-to-${to}.csv`
    );

  const exportZip = () => {
    const summary = toCsv(
      ["Metric", "Value"],
      [
        ["Period From", from],
        ["Period To", to],
        ["Applications", totals.count],
        ["Total Requirement", totals.requirement],
        ["Assistance Given", totals.given],
        ["Balance Recommended", totals.balance],
      ]
    );
    const categories = toCsv(
      ["Category", "Cases", "Given", "Requirement"],
      byCategory.map(([k, v]) => [k, v.count, v.given, v.requirement])
    );
    const months = toCsv(
      ["Month", "Cases", "Given"],
      byMonth.map(([k, v]) => [k, v.count, v.given])
    );
    const lettersCsv = toCsv(
      ["Organization", "Letter Date", "Valid Until", "Ref No", "Applicant"],
      (data?.letters ?? []).map(l => [
        l.organization_name,
        l.letter_date,
        l.valid_until,
        l.applications?.ref_no ?? "",
        l.applications?.applicant_name ?? "",
      ])
    );
    const categoryStatus = toCsv(
      [
        "Category",
        "Applications Received",
        "Forwarded to Institutions",
        "Pending",
        "Requirement",
        "Given",
      ],
      byCategoryStatus.map(([k, v]) => [
        k,
        v.received,
        v.forwarded,
        v.pending,
        v.requirement,
        v.given,
      ])
    );
    downloadZip(
      {
        "00-README.txt": [
          "Financial Assistance Report Bundle",
          `Period: ${from} to ${to}`,
          `Generated: ${new Date().toLocaleString()}`,
          "",
          "Files included:",
          "01-summary.csv       — period totals",
          "02-applications.csv  — full application-wise detail",
          "03-by-category.csv   — assistance grouped by category",
          "04-by-month.csv      — assistance grouped by month",
          "05-letters.csv       — recommendation letters issued",
          "06-category-status.csv — category-wise received vs forwarded",
        ].join("\n"),
        "01-summary.csv": summary,
        "02-applications.csv": toCsv(appHead, appRows()),
        "03-by-category.csv": categories,
        "04-by-month.csv": months,
        "05-letters.csv": lettersCsv,
        "06-category-status.csv": categoryStatus,
      },
      `assistance-report-${from}-to-${to}.zip`
    );
  };

  return (
    <Shell>
      <PageHeader
        title="Reports & Analytics"
        subtitle="Period-wise assistance summary for trustee review"
        actions={
          <>
            <Button onClick={exportZip}>
              <FileArchive className="mr-2 h-4 w-4" /> Share as ZIP
            </Button>
            <Button onClick={exportCsv} variant="outline">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button onClick={() => window.print()} variant="outline">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase">From</Label>
            <Input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase">To</Label>
            <Input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {shortDate(from)} — {shortDate(to)}
          </p>
        </CardContent>
      </Card>

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <Stat label="Applications" value={String(totals.count)} />
        <Stat label="Total Requirement" value={inr(totals.requirement)} />
        <Stat label="Assistance Given" value={inr(totals.given)} />
        <Stat label="Balance Recommended" value={inr(totals.balance)} />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="no-print mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="category">Category-wise Report</TabsTrigger>
        </TabsList>

        <TabsContent value="category" className="mt-0 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
              <CardTitle className="text-base">
                Category-wise Report — Received vs Forwarded ({shortDate(from)}{" "}
                — {shortDate(to)})
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                className="no-print"
                onClick={exportCategoryCsv}
              >
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr>
                    <th className="px-4 py-2">Category / Subcategory</th>
                    <th className="px-4 py-2 text-right">Received</th>
                    <th className="px-4 py-2 text-right">Forwarded</th>
                    <th className="px-4 py-2 text-right">Pending</th>
                    <th className="px-4 py-2 text-right">Forwarded %</th>
                    <th className="px-4 py-2 text-right">Requirement</th>
                    <th className="px-4 py-2 text-right">Assistance Given</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryReport.rows.map(([k, v]) => (
                    <Fragment key={k}>
                      <tr className="bg-muted/40 font-medium">
                        <td className="px-4 py-2">{k}</td>
                        <td className="px-4 py-2 text-right">{v.received}</td>
                        <td className="px-4 py-2 text-right">{v.forwarded}</td>
                        <td className="px-4 py-2 text-right">
                          {v.received - v.forwarded}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {v.received
                            ? Math.round((v.forwarded / v.received) * 100)
                            : 0}
                          %
                        </td>
                        <td className="px-4 py-2 text-right">
                          {inr(v.requirement)}
                        </td>
                        <td className="px-4 py-2 text-right">{inr(v.given)}</td>
                      </tr>
                      {[...v.subs.entries()].map(([sk, sv]) => (
                        <tr
                          key={`${k}-${sk}`}
                          className="text-muted-foreground"
                        >
                          <td className="px-4 py-2 pl-8">{sk}</td>
                          <td className="px-4 py-2 text-right">
                            {sv.received}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {sv.forwarded}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {sv.received - sv.forwarded}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {sv.received
                              ? Math.round((sv.forwarded / sv.received) * 100)
                              : 0}
                            %
                          </td>
                          <td className="px-4 py-2 text-right">—</td>
                          <td className="px-4 py-2 text-right">
                            {inr(sv.given)}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {!categoryReport.rows.length && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No applications in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
                {categoryReport.rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-primary/10 font-semibold">
                      <td className="px-4 py-2">Grand Total</td>
                      <td className="px-4 py-2 text-right">
                        {categoryReport.totals.received}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {categoryReport.totals.forwarded}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {categoryReport.totals.received -
                          categoryReport.totals.forwarded}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {categoryReport.totals.received
                          ? Math.round(
                              (categoryReport.totals.forwarded /
                                categoryReport.totals.received) *
                                100
                            )
                          : 0}
                        %
                      </td>
                      <td className="px-4 py-2 text-right">
                        {inr(categoryReport.totals.requirement)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {inr(categoryReport.totals.given)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="mt-0">
          <div className="grid gap-4 lg:grid-cols-3">
            <ReportTable
              title="By Category"
              head={["Category", "Cases", "Given"]}
              rows={byCategory.map(([k, v]) => [
                k,
                String(v.count),
                inr(v.given),
              ])}
            />
            <ReportTable
              title="By Month"
              head={["Month", "Cases", "Given"]}
              rows={byMonth.map(([k, v]) => [k, String(v.count), inr(v.given)])}
            />
            <ReportTable
              title="Letters by Organization"
              head={["Organization", "Letters", ""]}
              rows={byOrg.map(([k, v]) => [k, String(v), ""])}
            />
          </div>

          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Category-wise Summary (Received vs Forwarded)
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2 text-right">
                      Applications Received
                    </th>
                    <th className="px-4 py-2 text-right">
                      Forwarded to Institutions
                    </th>
                    <th className="px-4 py-2 text-right">
                      Pending / Not Forwarded
                    </th>
                    <th className="px-4 py-2 text-right">Total Requirement</th>
                    <th className="px-4 py-2 text-right">Assistance Given</th>
                  </tr>
                </thead>
                <tbody>
                  {byCategoryStatus.map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-4 py-2 font-medium">{k}</td>
                      <td className="px-4 py-2 text-right">{v.received}</td>
                      <td className="px-4 py-2 text-right">{v.forwarded}</td>
                      <td className="px-4 py-2 text-right">{v.pending}</td>
                      <td className="px-4 py-2 text-right">
                        {inr(v.requirement)}
                      </td>
                      <td className="px-4 py-2 text-right">{inr(v.given)}</td>
                    </tr>
                  ))}
                  {!byCategoryStatus.length && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No applications in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Application-wise Detail
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="data-table w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Ref</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2 text-right">Requirement</th>
                    <th className="px-4 py-2 text-right">Given</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="no-print px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {apps.map(a => (
                    <tr key={a.id} className="border-t border-border">
                      <td className="px-4 py-2 font-medium">{a.ref_no}</td>
                      <td className="px-4 py-2">
                        {shortDate(a.application_date)}
                      </td>
                      <td className="px-4 py-2">{a.applicant_name}</td>
                      <td className="px-4 py-2">
                        {a.assistance_categories?.name ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {inr(a.total_requirement)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {inr(a.our_contribution)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {inr(a.balance_required)}
                      </td>
                      <td className="px-4 py-2">{a.status}</td>
                      <td className="no-print px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpenId(a.id)}
                        >
                          <Eye className="mr-1 h-4 w-4" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!apps.length && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No applications in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(openApp)} onOpenChange={o => !o && setOpenId(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Application Ref. {openApp?.ref_no} — {openApp?.applicant_name}
            </DialogTitle>
          </DialogHeader>
          {openApp && (
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <Detail
                label="Application Date"
                value={shortDate(openApp.application_date)}
              />
              <Detail
                label="Ration Card"
                value={openApp.ration_card_no ?? "—"}
              />
              <Detail
                label="Category"
                value={openApp.assistance_categories?.name ?? "—"}
              />
              <Detail label="Subcategory" value={openApp.subcategory ?? "—"} />
              <Detail
                label="Beneficiary"
                value={openApp.beneficiary_name ?? openApp.applicant_name}
              />
              <Detail label="Mobile" value={openApp.applicant_mobile ?? "—"} />
              <div className="md:col-span-2">
                <Detail label="Purpose" value={openApp.purpose ?? "—"} />
              </div>
              <Detail
                label="A. Total Requirement"
                value={inr(openApp.total_requirement)}
              />
              <Detail
                label="B. Own Contribution"
                value={inr(openApp.applicant_contribution)}
              />
              <Detail
                label="C. Our Contribution"
                value={inr(openApp.our_contribution)}
              />
              <Detail
                label="D. Other Received"
                value={inr(openApp.other_received)}
              />
              <Detail
                label="E. Balance Required"
                value={inr(openApp.balance_required)}
              />
              <Detail label="Status" value={openApp.status} />
              <Detail label="Bank" value={openApp.bank_name ?? "—"} />
              <Detail
                label="Account No."
                value={openApp.bank_account_no ?? "—"}
              />
              <div className="md:col-span-2">
                <Detail
                  label="Address"
                  value={openApp.applicant_address ?? "—"}
                />
              </div>
              <div className="md:col-span-2">
                <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Letters issued in this period
                </p>
                <ul className="list-inside list-disc text-muted-foreground">
                  {(data?.letters ?? [])
                    .filter(l => l.application_id === openApp.id)
                    .map((l, i) => (
                      <li key={i}>
                        {l.organization_name} — {shortDate(l.letter_date)}{" "}
                        (valid till {shortDate(l.valid_until)})
                      </li>
                    ))}
                  {!(data?.letters ?? []).some(
                    l => l.application_id === openApp.id
                  ) && <li>No letters recorded.</li>}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-medium">{value}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="font-display mt-1 text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function ReportTable({
  title,
  head,
  rows,
}: {
  title: string;
  head: string[];
  rows: string[][];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
            <tr>
              {head.map(h => (
                <th key={h} className="px-4 py-2">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.join("-")} className="border-t border-border">
                {r.map((c, i) => (
                  <td
                    key={i}
                    className={i === 0 ? "px-4 py-2" : "px-4 py-2 text-right"}
                  >
                    {c}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td
                  colSpan={head.length}
                  className="px-4 py-6 text-center text-muted-foreground"
                >
                  No data for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
